import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey } from "assets/jss/common-styles";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { TextPluralization } from "components/TextPluralization";
import { FeedType } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Publisher from "views/Timeline/Components/CardComp/Publisher";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
}

interface IProps {
    data: FeedItemDto;

    /** Está em página secundária? */
    fullView: boolean;

    /** Tipo de Feed */
    feedType: FeedType;

    onUpdateCard: (newItemDto: DeepPartial<FeedItemDto>) => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class BodyPoll extends React.PureComponent<Props, IState> {

    ///
    private handleSelection(answerId: string) {

        const { data: { publicationId } } = this.props;

        api.postAnswer(publicationId, answerId)
            .then((result) => {
                this.setState({ editTopicDialog: false });
                this.props.onUpdateCard(result);
            })
            .catch((error) => {
                this.setState({ editTopicDialog: false });
            });
    }

    ///
    private renderExpireMessage() {
        const {
            t,
            data: {
                body: {
                    poll
                }
            }
        } = this.props;

        const now = moment();

        if (poll.allowAnswersUntil != null) {
            var expireMessage = null;
            var until = moment(poll.allowAnswersUntil);
            if (now < until) {
                var eod = (until.hours() === 23 && until.minutes() == 59 && until.seconds() == 59);
                if (until.diff(now, "days") > 0 || eod)
                    expireMessage = <MuiColor variant="warning">{t("Common.Feed.Poll.ExpiresText", { allowAnswersUntil: poll.allowAnswersUntil })}</MuiColor>;
                else
                    expireMessage = <MuiColor variant="error">{t("Common.Feed.Poll.ExpiresTodayText", { allowAnswersUntil: poll.allowAnswersUntil })}</MuiColor>;
            }
            else expireMessage = <MuiColor variant="muted">{t("Common.Feed.Poll.VoteFinished")}</MuiColor>;

            return <MuiContainer margin="dense">{expireMessage}</MuiContainer>;
        }

        return null;
    }

    ///
    private renderPoll() {
        const { classes, data: { body: { poll } } } = this.props;
        return (
            <div>
                {
                    poll.options.map((v, index) => {
                        return (
                            <MuiContainer>
                                <MuiClickable onClick={() => this.handleSelection(v.id)}>
                                    <div className={classes.pollOption}>
                                        {v.text}
                                    </div>
                                </MuiClickable>
                            </MuiContainer>
                        );
                    })
                }
                {this.renderExpireMessage()}
            </div>
        );
    }

    ///
    private renderPollResult() {
        const {
            t,
            classes,
            data: {
                body: {
                    poll
                },
                interaction: {
                    poll: {
                        pollSelectedOptionId
                    }
                }
            }
        } = this.props;

        const maxWidth = 580;
        let maxValue = 0;
        let totalAnswers = 0;

        if (poll.results == null) {
            poll.results = [];
        }
        else if (poll.results.length > 0) {
            maxValue = Math.max(...poll.results.map(x => x.total));
            totalAnswers = poll.results.map(x => x.total).reduce((p, c) => p + c);
        }

        var tableRows = poll.options.map((v, index) => {
            var r = poll.results.find(p => p.optionId == v.id);
            var w = r == null ? 0 : Math.round(r.total / totalAnswers * maxWidth);
            var p = r == null ? 0 : Math.round(r.total / totalAnswers * 100);
            const bold = r != null && r.total == maxValue;

            return (
                <tr key={index}>
                    <td>
                        <div className={classes.pollGraphContainer} style={{ width: maxWidth }}>
                            <div className={classes.pollGraphLabel}>
                                {
                                    bold
                                        ? <b>{v.text}</b>
                                        : <span>{v.text}</span>
                                }
                                {
                                    pollSelectedOptionId == v.id
                                        ? <small><MuiColor variant="white">&nbsp;<FontAwesomeIcon icon={faCheckCircle} /></MuiColor></small>
                                        : <span>&nbsp;</span>
                                }
                            </div>
                            <div className={bold ? classes.pollGraphBarWin : classes.pollGraphBar} style={{ width: w }}>
                                <span>&nbsp;</span>
                            </div>
                        </div>
                    </td>
                    <td className={classes.pollGraphPercent}>
                        {
                            bold
                                ? <b><span>{p}%</span></b>
                                : <span>{p}%</span>
                        }
                    </td>
                </tr>
            );
        });

        return (
            <MuiContainer>
                <MuiContainer>
                    <table className={classes.pollGraphTable}>
                        <tbody>
                            {tableRows}
                        </tbody>
                    </table>
                </MuiContainer>

                <MuiContainer>
                    <MuiMuted>
                        <div>
                            <span><TextPluralization count={totalAnswers} resource="Common.Feed.Poll.AnswersMessage" /></span>
                            <span>{poll.allowAnswersUntil == null ? ` ${t("Common.Feed.Poll.UntilThisMomentText")}.` : "."}</span>
                        </div>
                    </MuiMuted>
                </MuiContainer>

                {this.renderExpireMessage()}

            </MuiContainer>
        );
    }

    ///
    public render() {
        const {
            data: {
                interaction: {
                    poll: {
                        pollSelectedOptionId
                    }
                },
                body: {
                    poll: {
                        allowAnswersUntil
                    }
                }
            }
        } = this.props;


        return (
            <>
                <MuiContainer>
                    <Publisher {...this.props} />
                </MuiContainer>
                {
                    pollSelectedOptionId == null && (allowAnswersUntil == null || allowAnswersUntil > new Date())
                        ? this.renderPoll()
                        : this.renderPollResult()
                }
            </>
        );
    }
}

export default withTranslation()(BodyPoll);
